import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Check } from "lucide-react";
import { Reveal, SectionNo, Hairline, PlaceholderTag } from "@/components/primitives";
import MediaImage from "@/components/MediaImage";
import { site, placeholders } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  "Southeast Asia",
  "Indian Ocean",
  "Europe · Alps",
  "Europe · Mediterranean",
  "Middle East",
  "Americas",
  "Elsewhere",
];
const STAGES = ["Exploring", "Live project", "Approved to proceed"];
const LIBRARY_SIZES = ["Under 200 images", "200–1,000", "1,000–5,000", "5,000+", "Not sure"];
const INVESTMENT_BANDS = [
  "Under $2,500",
  "$2,500–$7,500",
  "$7,500–$20,000",
  "$20,000+",
  "Not yet set, advise me",
];

const EMPTY = {
  propertyName: "",
  name: "",
  role: "",
  email: "",
  region: "",
  stage: "",
  librarySize: "",
  investmentBand: "",
  objective: "",
  materialLink: "",
};

function validate(v) {
  const e = {};
  if (!v.propertyName.trim()) e.propertyName = "Please add the property or group name.";
  if (!v.name.trim()) e.name = "Please tell us your name.";
  if (!v.email.trim()) e.email = "We need an email to reply to.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) e.email = "That email doesn't look right.";
  if (!v.region) e.region = "Choose a region.";
  if (!v.stage) e.stage = "Where are you in the process?";
  if (!v.librarySize) e.librarySize = "A rough size is fine.";
  if (!v.investmentBand) e.investmentBand = "Required. It lets us reply with something useful.";
  if (!v.objective.trim()) e.objective = "One line is plenty.";
  return e;
}

const fieldCls =
  "w-full bg-transparent border-b border-hairline py-3 text-primaryText placeholder:text-mutedText/60 focus:border-gold focus-visible:outline-none transition-colors duration-300";

function Field({ label, error, children, required }) {
  return (
    <div>
      <label className="eyebrow mb-3 block">
        {label}
        {required && <span className="ml-1 text-gold" aria-hidden="true">*</span>}
      </label>
      {children}
      {error && (
        <p className="mt-2 text-xs text-[#c98b6c]" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function Select({ value, onChange, options, invalid, testid, labelId }) {
  return (
    <select
      className={`${fieldCls} appearance-none`}
      value={value}
      onChange={onChange}
      aria-invalid={invalid}
      aria-label={labelId}
      data-testid={testid}
    >
      <option value="" disabled className="bg-ink">
        Select
      </option>
      {options.map((o) => (
        <option key={o} value={o} className="bg-ink">
          {o}
        </option>
      ))}
    </select>
  );
}

export default function Enquiry() {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length) {
      const first = document.querySelector('[aria-invalid="true"]');
      if (first) first.focus();
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, values);
      setDone(true);
    } catch (err) {
      setErrors({ form: "Something interrupted the send. Please try once more." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="enquiry"
      className="on-dark relative overflow-hidden px-6 py-20 sm:px-10 md:py-24"
      data-testid="enquiry"
    >
      <div className="absolute inset-0" aria-hidden="true">
        <MediaImage
          id="S12"
          sizes="100vw"
          className="atmos h-full w-full object-cover opacity-30"
        />
      </div>
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, #12100e 0%, rgba(18,16,14,0.85) 40%, #12100e 100%)",
        }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-[1080px]">
        <Hairline className="mb-20" />
        <Reveal>
          <SectionNo n="11" label="Enquiry" />
        </Reveal>

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-16 flex flex-col items-center rounded-[3px] border border-brass/25 bg-ink/60 px-8 py-24 text-center"
            data-testid="enquiry-success"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-gold/50 text-gold">
              <Check size={28} strokeWidth={1.4} />
            </div>
            <h2
              className="display mt-8 text-primaryText"
              style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)", fontWeight: 300 }}
            >
              Your enquiry is with us.
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-mutedText">
              We read every enquiry ourselves and reply within{" "}
              {placeholders.responseTime}
              <PlaceholderTag />. A considered answer, not a template.
            </p>
          </motion.div>
        ) : (
          <div className="mt-8 grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start lg:gap-20">
            <div className="lg:sticky lg:top-28">
              <Reveal>
                <h2
                  className="display text-primaryText"
                  style={{
                    fontSize: "clamp(2.5rem, 5.5vw, 4.25rem)",
                    lineHeight: 1,
                    letterSpacing: "-0.02em",
                    fontWeight: 300,
                  }}
                >
                  See your property move.
                </h2>
              </Reveal>
              <Reveal delay={0.08}>
                <p className="mt-8 max-w-md text-base leading-relaxed text-mutedText sm:text-lg">
                  Tell us where you are and what you're working with. We reply
                  within {placeholders.responseTime}
                  <PlaceholderTag /> with an honest read on your library: what
                  will move well, and what won't.
                </p>
              </Reveal>
              <Reveal delay={0.14}>
                <p className="mt-8 text-sm text-mutedText">
                  Prefer to talk?{" "}
                  <a
                    href={`mailto:${site.email}`}
                    className="link-wipe text-primaryText"
                    data-testid="enquiry-email"
                  >
                    Email us directly
                  </a>
                </p>
              </Reveal>
            </div>

            <Reveal delay={0.05}>
              <form onSubmit={onSubmit} noValidate className="space-y-8" data-testid="enquiry-form">
                <Field label="Property or group name" error={errors.propertyName} required>
                  <input
                    className={fieldCls}
                    value={values.propertyName}
                    onChange={set("propertyName")}
                    aria-invalid={!!errors.propertyName}
                    placeholder="The property"
                    data-testid="field-property"
                  />
                </Field>

                <div className="grid gap-8 sm:grid-cols-2">
                  <Field label="Your name" error={errors.name} required>
                    <input
                      className={fieldCls}
                      value={values.name}
                      onChange={set("name")}
                      aria-invalid={!!errors.name}
                      placeholder="Your name"
                      data-testid="field-name"
                    />
                  </Field>
                  <Field label="Role">
                    <input
                      className={fieldCls}
                      value={values.role}
                      onChange={set("role")}
                      placeholder="GM, owner, marketing…"
                      data-testid="field-role"
                    />
                  </Field>
                </div>

                <Field label="Email" error={errors.email} required>
                  <input
                    type="email"
                    className={fieldCls}
                    value={values.email}
                    onChange={set("email")}
                    aria-invalid={!!errors.email}
                    placeholder="you@property.com"
                    data-testid="field-email"
                  />
                </Field>

                <div className="grid gap-8 sm:grid-cols-2">
                  <Field label="Region" error={errors.region} required>
                    <Select
                      value={values.region}
                      onChange={set("region")}
                      options={REGIONS}
                      invalid={!!errors.region}
                      labelId="Region"
                      testid="field-region"
                    />
                  </Field>
                  <Field label="Where are you in the process?" error={errors.stage} required>
                    <Select
                      value={values.stage}
                      onChange={set("stage")}
                      options={STAGES}
                      invalid={!!errors.stage}
                      labelId="Stage"
                      testid="field-stage"
                    />
                  </Field>
                </div>

                <div className="grid gap-8 sm:grid-cols-2">
                  <Field label="Size of your photo library" error={errors.librarySize} required>
                    <Select
                      value={values.librarySize}
                      onChange={set("librarySize")}
                      options={LIBRARY_SIZES}
                      invalid={!!errors.librarySize}
                      labelId="Library size"
                      testid="field-library"
                    />
                  </Field>
                  <Field label="Investment range" error={errors.investmentBand} required>
                    <Select
                      value={values.investmentBand}
                      onChange={set("investmentBand")}
                      options={INVESTMENT_BANDS}
                      invalid={!!errors.investmentBand}
                      labelId="Investment range"
                      testid="field-investment"
                    />
                    <p className="mt-3 text-xs italic leading-relaxed text-mutedText/80">
                      Most projects fall between $2,500 and $12,000. The range helps us shape the right proposal.
                    </p>
                  </Field>
                </div>

                <Field label="What should these films do for you?" error={errors.objective} required>
                  <textarea
                    rows={3}
                    className={`${fieldCls} resize-none`}
                    value={values.objective}
                    onChange={set("objective")}
                    aria-invalid={!!errors.objective}
                    placeholder="A refreshed website, a season launch, a channel that finally moves…"
                    data-testid="field-objective"
                  />
                </Field>
                <Field label="Link to your photography (optional)">
                  <input
                    className={fieldCls}
                    value={values.materialLink}
                    onChange={set("materialLink")}
                    placeholder="Shared folder, site or listing link"
                    data-testid="field-link"
                  />
                </Field>

                {errors.form && (
                  <p className="text-sm text-[#c98b6c]" role="alert">
                    {errors.form}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-sweep w-full rounded-full bg-brass py-4 text-sm font-medium tracking-wide text-ink transition-colors duration-300 hover:bg-[#c9aa6c] disabled:opacity-60"
                  data-testid="enquiry-submit"
                >
                  {submitting ? "Sending…" : "Request a proposal"}
                </button>
              </form>
            </Reveal>
          </div>
        )}
      </div>
    </section>
  );
}
